import React, { useCallback, useEffect, useState } from "react";
import { HomeTabsParams } from "../../navigation/AppStack/HomeTabParams";
import {View, StyleSheet, ActivityIndicator, ScrollView, Text} from "react-native";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { AppTheme, useTheme } from "../../theme/colors";
import { Button } from "../../components";
import {ButtonType, IsMobile} from "../../theme";
import WalletIcon from "../../Icons/WalletIcon";
import HeaderWrap from "../../components/Header";
import { styles } from "../../theme/styles";
import { ProfileStackRoute, WalletStackRoute } from "../../navigation/AppStack/routes";
import {
  PassportFileShareScreenRoute,
  UploadFileScreenRoute,
} from "../../navigation/WalletStack/routes";

import FolderAndFilesListing from "../../components/FolderAndFilesListing";
import {
  deletePassportFileById,
  getPassportFilesByPassportId,
  updatePassportFileById,
} from "../../services/NetworkManager";
import { Storage } from "../../utils/Storage";
import { useAppContext } from "../../contexts/AppContext";
import CustomAlert from "../../components/CoustmizeAlertmsg";
import useAuth from "../../contexts/Auth/useAuth";
import { deletePassportFile } from "../../utils/API";
import FileItem from "../../components/FileItem";
import { ShareScreenRoute } from "../../navigation/ProfileStack/routes";
import {getSession} from "../../utils/cognitoAuth";
import {WEB_MAXWIDTH} from "../../../constants"; // Import getSession for fetching the token


export interface FileData {
  id: string;
  folder: string;
  title: string;
  url: string;
}

interface GroupedFiles {
  [folder: string]: FileData[];
}

const passport: FileData = {
  id: "",
  folder: "",
  title: "Passport",
  url: "",
};

const WalletScreen = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [isAlertVisible, setAlertVisibility] = useState<boolean>(false);
  const [Message, setMessage] = useState<string>("");
  const [fileToDelete, setFileToDelete] = useState<FileData | null>(null);
  const [groupedFiles, setGroupedFiles] = useState({});
  const navigation = useNavigation<NavigationProp<HomeTabsParams>>();
  const theme = useTheme();
  const { setPassportFile } = useAppContext();
  const { user } = useAuth();
  const customStyles = getStyles(theme);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      fetchData();
    });

    // Cleanup function
    return unsubscribe;
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const id = (await Storage.getValue("passportId")) as string;
      const response = await getPassportFilesByPassportId(id);
      const passportFiles = response.items.map((file: any) => ({
        id: file.id,
        folder: file.folder,
        title: file.title,
        url: file.url,
      }));
      const groupedFiles = passportFiles.reduce(
        (acc: GroupedFiles, curr: FileData) => {
          if (!acc[curr.folder]) {
            acc[curr.folder] = [];
          }
          acc[curr.folder].push(curr);
          return acc;
        },
        {}
      );
      setGroupedFiles(groupedFiles);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setMessage("Error! could not retrieve the passport files data.");
      setAlertVisibility(true);
      console.error("Error fetching folder data:", error);
    }
  }, []);

  const handlePassportPress = () => {
    navigation.navigate(ProfileStackRoute, {
      screen: ShareScreenRoute,
    });
  }

  const handleUpload = useCallback(() => {
    navigation.navigate(WalletStackRoute, {
      screen: UploadFileScreenRoute,
      params: undefined
    });
  }, []);

  const handleFilePress = (file: FileData) => {
    setPassportFile(file);
    navigation.navigate(WalletStackRoute, {
      screen: PassportFileShareScreenRoute,
      params: undefined

    });
  };

  const handleEditFileTitle = async (id: string, title: string) => {
    try {
      setLoading(true);
      await updatePassportFileById({
        id: id,
        title: title,
      });
      setMessage("Title Updated Successfully.");
      setAlertVisibility(true);
      await fetchData();
    } catch (error) {
      setLoading(false);
      setMessage("Title Updating Failed");
      setAlertVisibility(true);
      console.log("There is error while Editing the file Title.");
    }
  };

  const handleDeleteFile = (file: FileData) => {
    setFileToDelete(file);
    setMessage("Are you sure you want to delete this file.");
    setAlertVisibility(true);
  };

  const handleDeleteFileConfirmation = async () => {
    try {
      const session = await getSession();
      if (!session) throw new Error("Session not available");

      const token = session.accessToken;

      setLoading(true);
      if (fileToDelete && user) {
        await deletePassportFileById(fileToDelete.id);
        await deletePassportFile(user.username, fileToDelete.url, token);
        setMessage("File Deleted Successfully.");
        setAlertVisibility(true);
        await fetchData();
      }
    } catch (error) {
      setLoading(false);
      setMessage("Error! could not delete the passport file.");
      setAlertVisibility(true);
      console.log("There is error while deleting the file.");
    }
  };

  return loading ? (
    <View
      style={[
        styles.flex1,
        styles.center,
        { backgroundColor: theme.BACKGROUND },
        !IsMobile && { width: WEB_MAXWIDTH, alignSelf: "center"}
      ]}
    >
      <ActivityIndicator size="large" color={theme.ACCENT} />
    </View>
  ) : (
    <HeaderWrap
      Icon={
        <WalletIcon
          transform={[{ translateX: -10 }]}
          color={theme.WHITE}
          width={40}
          height={40}
        />
      }
      title={`One Vault`}
      showShareIcon={false}
    >
      <View
        style={[
          styles.flex1,
          { backgroundColor: theme.BACKGROUND, paddingBottom: 20 },
          !IsMobile && { width: WEB_MAXWIDTH, alignSelf: "center"}
        ]}
      >
        <ScrollView
          style={{ flex: 1 }}
          scrollEventThrottle={1600}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ padding: 20, paddingBottom: 60 }}
        >
          <Text style={styles.title}>One Vault</Text>
          <View style={customStyles.passport}>
            <FileItem file={passport} handleFilePress={handlePassportPress} />
          </View>
          <View style={[styles.flex1, { backgroundColor: theme.BACKGROUND }]}>
            {Object.entries(groupedFiles).map(([folder, files], index) => (
              <FolderAndFilesListing
                key={index}
                folder={folder}
                files={files as FileData[]}
                handleFilePress={handleFilePress}
                handleEditFileTitle={handleEditFileTitle}
                handleDeleteFile={handleDeleteFile}
              />
            ))}
          </View>
        </ScrollView>
        <View
          style={[{ backgroundColor: theme.BACKGROUND }, customStyles.button]}
        >
          <Button
            title={"Upload"}
            onPress={handleUpload}
            variant={ButtonType.secondary}
          />
        </View>
        <CustomAlert
          cancelText="cancel"
          confirmText="OK"
          visible={isAlertVisible}
          showCancelButton={fileToDelete !== null}
          title=""
          message={Message}
          onCancel={() => {
            setAlertVisibility(false);
            setFileToDelete(null);
          }}
          onConfirm={async () => {
            setAlertVisibility(false);
            if (fileToDelete) await handleDeleteFileConfirmation();
            setFileToDelete(null);
          }}
        />
      </View>
    </HeaderWrap>
  );
};

export default WalletScreen;

const getStyles = (theme: AppTheme) =>
  StyleSheet.create({
    button: {
      paddingTop: 20,
      justifyContent: "center",
      alignItems: "center",
    },
    passport: {
      marginLeft: 30,
    },
  });
